import '@/App.css';
import { HashRouter as Router, Routes, Route, Navigate, useParams, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { lazy, Suspense, useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GoogleSignIn } from '@capawesome/capacitor-google-sign-in';
import { Loader2 } from 'lucide-react';

const PageLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[400px]">
    <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--green-bright))]" />
    <span className="ml-3 text-muted-foreground font-medium">Loading page...</span>
  </div>
);

// UI Components
import Layout from '@/components/Layout';
import { Toaster } from '@/components/ui/sonner';
import { Toaster as ShadcnToaster } from '@/components/ui/toaster';
import ErrorBoundary from '@/components/ErrorBoundary';
import GlobalCelebration from '@/components/GlobalCelebration';
import { ProfileCompletionCheck } from '@/components/auth/ProfileCompletionCheck';
import UserGuideModal from '@/components/UserGuideModal';
import PromoModal from '@/components/PromoModal';
import UpdateAppModal from '@/components/UpdateAppModal';
import TeacherRatingModal from '@/components/tutor/TeacherRatingModal';
import OfflineBanner from '@/components/common/OfflineBanner';

// Auth & Parent Pages
const Login = lazy(() => import('@/pages/LoginNew'));
const Signup = lazy(() => import('@/pages/SignupNew'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const AnimationPreview = lazy(() => import('@/pages/AnimationPreview'));
const SimLab = lazy(() => import('@/pages/SimLab'));
const SubscriptionSuccess = lazy(() => import('@/pages/SubscriptionSuccess'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const ParentPlansPage = lazy(() => import('@/pages/parent/ParentPlansPage'));
const ParentAnalyticsPage = lazy(() => import('@/pages/parent/ParentAnalyticsPage'));

// Student Pages
const LearnPage = lazy(loadStudentLearnPage);
const MathProblemsPage = lazy(loadStudentMathProblemsPage);
const CuriosityCentre = lazy(loadStudentCuriosityCentre);
const AdvancedTopicsPage = lazy(loadStudentAdvancedTopicsPage);
const ArticleDetailPage = lazy(loadStudentArticleDetailPage);
const CourseContentPage = lazy(loadStudentCourseContentPage);
const ChapterCuriosity = lazy(loadStudentChapterCuriosity);
const VideoPlayer = lazy(loadStudentVideoPlayer);
const AudioPlayer = lazy(loadStudentAudioPlayer);
const ContentDetail = lazy(loadStudentContentDetailPage);
const ChatPage = lazy(loadStudentChatPage);
const ProfilePage = lazy(loadStudentProfilePage);
const LeaderboardPage = lazy(loadStudentLeaderboardPage);
const ShopPage = lazy(loadStudentShopPage);
const SettingsPage = lazy(loadStudentSettingsPage);
const TuitionsPage = lazy(loadStudentTuitionsPage);
const ShareLinkHandler = lazy(() => import('@/pages/student/ShareLinkHandler'));
const StudentFoundersPage = lazy(loadStudentFoundersPage);
const StudentAnalyticsPage = lazy(loadStudentAnalyticsPage);

// Lazy Load Quest Pages
const QuestsPage = lazy(loadStudentQuestsPage);
const PracticeModePage = lazy(loadStudentPracticeModePage);
const TestModePage = lazy(loadStudentTestModePage);
const ResultsPage = lazy(loadStudentResultsPage);

// Teacher Pages
const TeacherAITools = lazy(() => import('@/pages/teacher/TeacherAITools'));
const TeacherCalendar = lazy(() => import('@/pages/teacher/TeacherCalendar'));
const TeacherLibrary = lazy(() => import('@/pages/teacher/TeacherLibrary'));
const TeacherNotes = lazy(() => import('@/pages/teacher/TeacherNotes'));
const TeacherProfile = lazy(() => import('@/pages/teacher/TeacherProfile'));
const QuestionGenerator = lazy(() => import('@/pages/teacher/QuestionGenerator'));
const ChapterPlannerList = lazy(() => import('@/pages/teacher/ChapterPlannerList'));
const ChapterTeachingPlanner = lazy(() => import('@/pages/teacher/ChapterTeachingPlanner'));
const TeacherEarnings = lazy(() => import('@/pages/teacher/TeacherEarnings'));
const TeacherSessions = lazy(() => import('@/pages/teacher/TeacherSessions'));
const TeacherSettings = lazy(() => import('@/pages/teacher/TeacherSettings'));
const TeacherTrainings = lazy(() => import('@/pages/teacher/TeacherTrainings'));

// B2B Portal Pages
const B2BLayout = lazy(() => import('@/pages/b2b/B2BLayout'));
const B2BDashboard = lazy(() => import('@/pages/b2b/B2BDashboard'));
const B2BStudents = lazy(() => import('@/pages/b2b/B2BStudents'));
const B2BStaff = lazy(() => import('@/pages/b2b/B2BStaff'));
const B2BStudentAnalytics = lazy(() => import('@/pages/b2b/B2BStudentAnalytics'));
const B2BCoupons = lazy(() => import('@/pages/b2b/affiliate/B2BCoupons'));
const B2BCommissions = lazy(() => import('@/pages/b2b/affiliate/B2BCommissions'));
const B2BProfile = lazy(() => import('@/pages/b2b/B2BProfile'));

// Affiliate Portal Pages
const AffiliateLayout = lazy(() => import('@/pages/affiliate/AffiliateLayout'));
const AffiliateDashboard = lazy(() => import('@/pages/affiliate/AffiliateDashboard'));
const AffiliateCommissions = lazy(() => import('@/pages/affiliate/AffiliateCommissions'));
const AffiliateCoupons = lazy(() => import('@/pages/affiliate/AffiliateCoupons'));
const AffiliateSettings = lazy(() => import('@/pages/affiliate/AffiliateSettings'));
const AffiliatorAuth = lazy(() => import('@/pages/affiliate/AffiliatorAuth'));
const StudentAffiliateEarnings = lazy(() => import('@/pages/student/StudentAffiliateEarnings'));
const TeacherAffiliateEarnings = lazy(() => import('@/pages/teacher/TeacherAffiliateEarnings'));

// Guardian Portal Pages
const GuardianLayout = lazy(() => import('@/pages/guardian/GuardianLayout'));
const GuardianDashboard = lazy(() => import('@/pages/guardian/GuardianDashboard'));
const GuardianChildAnalytics = lazy(() => import('@/pages/guardian/GuardianChildAnalytics'));

const AboutPage = lazy(loadAboutPage);

// PWA components
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import PortraitLock from '@/components/PortraitLock';

// Context Providers
import { QuestsProvider } from '@/context/QuestsContext';
import { SubjectChapterProvider } from '@/context/SubjectChapterContext';
import { TeacherProvider } from '@/context/TeacherContext';
import { PointsProvider } from '@/context/PointsContext';
import { PreferencesProvider } from '@/context/PreferencesContext';
import { UserProfileProvider, useUserProfile } from '@/context/UserProfileContext';
import { DiscoveryProvider } from '@/context/DiscoveryContext';
import { ComplianceProvider } from '@/context/ComplianceContext';
import { LiveClassProvider, useLiveClass } from '@/context/LiveClassContext';
import { LanguageProvider } from '@/context/LanguageContext';
import TutorLiveClassModal from '@/components/tutor/TutorLiveClassModal';
import { initAPI } from '@/api/init';

// Auth Utilities
import { SplashScreen } from '@capacitor/splash-screen';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { hasAnySession, getActivePortal, getHomePagePath, isAuthenticated, getPortalType, getUserData, clearAllSessions, hasAnyStoredToken } from '@/utils/auth';
import { hasAnonymousOnboarding, getAnonymousUserPreferences } from '@/utils/anonymousAuth';
import { preferencesAPI } from '@/api/preferences';
import { usePreferences } from '@/context/PreferencesContext';
import { toast } from 'sonner';
import {
  loadAboutPage,
  loadStudentAdvancedTopicsPage,
  loadStudentAnalyticsPage,
  loadStudentArticleDetailPage,
  loadStudentAudioPlayer,
  loadStudentChapterCuriosity,
  loadStudentChatPage,
  loadStudentContentDetailPage,
  loadStudentCourseContentPage,
  loadStudentCuriosityCentre,
  loadStudentFoundersPage,
  loadStudentLeaderboardPage,
  loadStudentLearnPage,
  loadStudentMathProblemsPage,
  loadStudentPracticeModePage,
  loadStudentProfilePage,
  loadStudentQuestsPage,
  loadStudentResultsPage,
  loadStudentSettingsPage,
  loadStudentShopPage,
  loadStudentTestModePage,
  loadStudentTuitionsPage,
  loadStudentVideoPlayer,
} from '@/routes/studentRoutePreloaders';

// Protected Route Component
function ProtectedRoute({ children }) {
  const location = useLocation();
  const path = location.pathname || '';
  const portal = getPortalType(path);

  // Allow guest access for certain routes
  const isGuest = !isAuthenticated(portal);
  const isGuestSession = localStorage.getItem('is_guest_session') === 'true';

  // Check if this is a route that allows guest access
  const allowGuestRoutes = [
    /^\/student\/achievements\/leaderboard(\/)?$/,  // Leaderboard
    /^\/student\/achievements\/shop(\/)?$/,          // Shop
    /^\/student\/achievements\/founders(\/)?$/,      // Founders
    /^\/student\/about(\/)?$/,                       // About
    /^\/student\/settings(\/)?$/,                    // Settings
    /^\/student\/profile(\/)?$/,                     // Profile (allow guests to see empty/prompt profile)
  ];

  const guestAllowedRoute = allowGuestRoutes.some(pattern => pattern.test(path));

  const guestQuestRoute = (() => {
    // Match /student/quests/:questId and /student/quests/:questId/(practice|test|results)
    const match = path.match(/^\/student\/quests\/([^\/]+)(?:\/(practice|test|results))?(\/)?$/);
    if (!match) return false;
    const questId = match[1];
    // Allow if questId looks like a UUID or exists in localStorage cache
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    const looksUuid = uuidRegex.test(questId);
    let inCache = false;
    try {
      const guestQuests = JSON.parse(localStorage.getItem('guest_quests') || '{}');
      inCache = !!guestQuests[questId];
    } catch (_) {
      inCache = false;
    }
    return looksUuid || inCache;
  })();

  // Allow if explicitly in guest session or on an allowed guest route
  if (isGuestSession || (isGuest && (guestQuestRoute || guestAllowedRoute))) {
    return children;
  }

  if (!isAuthenticated(portal)) {
    const fullTarget = location.pathname + (location.search || '');
    if (portal === 'affiliate') {
      return <Navigate to={`/affiliators/login?redirect=${encodeURIComponent(fullTarget)}`} state={{ from: location }} replace />;
    }
    return <Navigate to={`/login?redirect=${encodeURIComponent(fullTarget)}`} state={{ from: location }} replace />;
  }
  return children;
}

// Root Redirect Component
function RootRedirect() {
  const location = useLocation();
  const rawSearch = location.search || window.location.search || '';
  const searchParams = new URLSearchParams(rawSearch);
  const token = searchParams.get('token');

  // If a parent promo token is present on root URL, route directly to Parent Plans page
  if (token) {
    return <Navigate to={`/parent/plans${rawSearch.startsWith('?') ? rawSearch : `?${rawSearch}`}`} replace />;
  }

  // Defensive check: If pathname or hash indicates an affiliator route, route to affiliator partner auth
  const rawPath = (window.location.pathname || '').toLowerCase();
  const rawHash = (window.location.hash || '').toLowerCase();
  if (
    rawPath.startsWith('/affiliator') ||
    rawPath.startsWith('/affliator') ||
    rawHash.startsWith('#/affiliator') ||
    rawHash.startsWith('#/affliator')
  ) {
    if (rawHash.includes('signup') || rawPath.includes('signup')) {
      return <Navigate to="/affiliators/signup" replace />;
    }
    if (rawHash.includes('login') || rawPath.includes('login')) {
      return <Navigate to="/affiliators/login" replace />;
    }
    return <Navigate to="/affiliators" replace />;
  }

  if (rawPath.startsWith('/affiliate') || rawPath.startsWith('/affliate')) {
    return <Navigate to="/affiliate" replace />;
  }

  if (rawPath.startsWith('/book')) {
    const tutorIdMatch = rawPath.match(/^\/book\/([^\/?#]+)/);
    const tutorId = tutorIdMatch ? tutorIdMatch[1] : null;
    const targetUrl = tutorId ? `/student/tuitions?tutor=${encodeURIComponent(tutorId)}` : '/student/tuitions';
    if (!isAuthenticated('student')) {
      try {
        sessionStorage.setItem('post_auth_redirect', targetUrl);
      } catch (_) {}
      return <Navigate to={`/login?redirect=${encodeURIComponent(targetUrl)}`} replace />;
    }
    return <Navigate to={targetUrl} replace />;
  }

  if (hasAnySession()) {
    const activePortal = getActivePortal();
    if (activePortal) {
      return <Navigate to={getHomePagePath(activePortal)} replace />;
    }
  }

  // Check if guest session is active
  const isGuestSession = localStorage.getItem('is_guest_session') === 'true';
  if (isGuestSession) {
    return <Navigate to="/student/learn" replace />;
  }

  // Default to login as the entry point
  return <Navigate to="/login" replace />;
}

function LegacyCuriosityTopicRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/student/curiosity/topics/${id}` : '/student/curiosity/topics'} replace />;
}

function TuitionsRedirect() {
  const location = useLocation();
  const search = location.search || window.location.search || '';
  return <Navigate to={`/student/tuitions${search}`} replace />;
}

function BookTutorRedirect() {
  const { tutorId } = useParams();
  const location = useLocation();
  const targetUrl = tutorId ? `/student/tuitions?tutor=${encodeURIComponent(tutorId)}` : '/student/tuitions';
  
  if (!isAuthenticated('student')) {
    try {
      sessionStorage.setItem('post_auth_redirect', targetUrl);
    } catch (_) {}
    return <Navigate to={`/login?redirect=${encodeURIComponent(targetUrl)}`} state={{ from: location }} replace />;
  }
  return <Navigate to={targetUrl} replace />;
}

// Login Route Component
function LoginRoute() {
  if (hasAnySession()) {
    const activePortal = getActivePortal();
    if (activePortal) {
      return <Navigate to={getHomePagePath(activePortal)} replace />;
    }
  }
  return <Login />;
}

function Bootstrap({ children }) {
  const [initData, setInitData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const BOOTSTRAP_LOCK_KEY = 'bootstrap_lock';
    const BOOTSTRAP_DATA_KEY = 'bootstrap_init_data_v2';
    const CACHE_TTL = 2 * 60 * 1000; // 2 minutes

    async function doInit() {
      if (!isMounted) return;

      try {
        const portal = getActivePortal();

        // Even if not authenticated locally, we should try to fetch init state
        // to verify session, detect stale tokens (self-purge), and get discovery data for guests.
        // If we have any token-like thing, call the backend once.
        const hasLegacyOrAnyToken = hasAnyStoredToken();

        if (!portal && !hasLegacyOrAnyToken) {
          console.log('[Bootstrap] No session indicators found, continuing as guest.');
          if (isMounted) setLoading(false);
          return;
        }

        // 1. Try to use cached data if fresh
        const cachedRaw = localStorage.getItem(BOOTSTRAP_DATA_KEY);
        if (cachedRaw) {
          try {
            const cached = JSON.parse(cachedRaw);
            const isTeacherPortal = portal === 'teacher';
            const cacheHasTutorKey = cached?.data?.profile && ('is_tutor_configured' in cached.data.profile);
            if (Date.now() - cached.timestamp < CACHE_TTL && (!isTeacherPortal || cacheHasTutorKey)) {
              console.log('[Bootstrap] Using cached init data (fresh)');
              if (isMounted) {
                setInitData(cached.data);
                setLoading(false);
                return;
              }
            }
          } catch (_) { /* ignore parse error */ }
        }

        // 2. Multi-tab Lock Check
        const lockRaw = localStorage.getItem(BOOTSTRAP_LOCK_KEY);
        if (lockRaw) {
          try {
            const lock = JSON.parse(lockRaw);
            // If lock is fresh (< 10s), wait and poll
            if (Date.now() - lock.timestamp < 10000) {
              console.log('[Bootstrap] Another tab is initializing, waiting...');
              setTimeout(() => {
                if (isMounted) doInit();
              }, 1000);
              return; // DO NOT reach the finally block that clears the lock!
            }
          } catch (_) { /* ignore */ }
        }

        // 3. Acquire Lock and Fetch
        localStorage.setItem(BOOTSTRAP_LOCK_KEY, JSON.stringify({
          timestamp: Date.now(),
          tabId: Math.random().toString(36).substring(7)
        }));

        try {
          console.log('[Bootstrap] Fetching fresh initial state from server...');
          const data = await initAPI.getInitialState(portal);

          if (data?.should_clear_session) {
            console.warn('[Bootstrap] Server requested session purge. Stale tokens detected.');
            clearAllSessions();
            window.location.reload();
            return;
          }

          if (data && isMounted) {
            // Cache the response for other tabs
            localStorage.setItem(BOOTSTRAP_DATA_KEY, JSON.stringify({
              data,
              timestamp: Date.now()
            }));

            setInitData(data);

            if (data.profile) {
              const userKey = portal === 'teacher' ? 'teacher_user' : 'student_user';
              const currentRaw = localStorage.getItem(userKey);
              const current = currentRaw ? JSON.parse(currentRaw) : {};
              localStorage.setItem(userKey, JSON.stringify({ ...current, ...data.profile }));
              window.dispatchEvent(new CustomEvent('profileUpdated'));

              // Synchronize student flat academic cache keys
              if (portal === 'student') {
                const rawBid = data.profile.board?.preview_board_id || data.profile.board?.id || data.profile.board_id;
                const rawGid = data.profile.grade?.preview_grade_id || data.profile.grade?.id || data.profile.grade_id;
                const bid = (rawBid && String(rawBid) !== 'other') ? String(rawBid) : (data.profile.is_custom_curriculum ? '1' : '');
                const gid = (rawGid && String(rawGid) !== 'custom') ? String(rawGid) : (data.profile.is_custom_curriculum ? '5' : '');
                if (bid) localStorage.setItem('student_board_id', String(bid));
                if (gid) localStorage.setItem('student_grade_id', String(gid));

                const boardName = data.profile.board?.board_name || data.profile.board_name;
                const boardCode = data.profile.board?.board_code || data.profile.board_code;
                const gradeName = data.profile.grade?.grade_name || data.profile.grade_name;
                if (boardName) localStorage.setItem('student_board_name', boardName);
                if (boardCode) localStorage.setItem('student_board_code', boardCode);
                if (gradeName) localStorage.setItem('student_grade_name', gradeName);
              }
            }
          }
        } catch (err) {
          console.error('[Bootstrap] Initialization fetch failed:', err);
        } finally {
          // Only the tab that ACQUIRED the lock should REMOVE it
          localStorage.removeItem(BOOTSTRAP_LOCK_KEY);
          if (isMounted) setLoading(false);
        }
      } catch (globalErr) {
        console.error('[Bootstrap] Global initialization error:', globalErr);
        if (isMounted) setLoading(false);
      }
    }

    doInit();

    // Listen for storage events to pick up data from other tabs during polling
    const handleStorage = (e) => {
      if (e.key === BOOTSTRAP_DATA_KEY && e.newValue) {
        try {
          const cached = JSON.parse(e.newValue);
          if (Date.now() - cached.timestamp < CACHE_TTL) {
            console.log('[Bootstrap] Picked up init data from sibling tab');
            setInitData(cached.data);
            setLoading(false);
          }
        } catch (_) { }
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('userLoggedIn', doInit);

    return () => {
      isMounted = false;
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('userLoggedIn', doInit);
    };
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background z-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground font-medium animate-pulse">Initializing your experience...</p>
        </div>
      </div>
    );
  }

  return (
    <PreferencesProvider initialData={initData?.preferences}>
      <DiscoveryProvider initialData={initData}>
        <UserProfileProvider initialData={initData}>
          <PointsProvider initialData={initData?.points}>
            <ComplianceProvider>
              {children(initData)}
            </ComplianceProvider>
          </PointsProvider>
        </UserProfileProvider>
      </DiscoveryProvider>
    </PreferencesProvider>
  );
}

function App() {
  const googleClientId = (typeof window !== 'undefined' && window.__ENV && window.__ENV.REACT_APP_GOOGLE_CLIENT_ID) || process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const enableLogs = (typeof window !== 'undefined' && window.__ENV && window.__ENV.REACT_APP_ENABLE_LOGS) || process.env.REACT_APP_ENABLE_LOGS;

  const [toastPosition, setToastPosition] = useState(typeof window !== 'undefined' && window.innerWidth >= 768 ? "bottom-right" : "bottom-center");

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      setToastPosition(window.innerWidth >= 768 ? "bottom-right" : "bottom-center");
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Top-level Splash Screen Failsafe: ensures splash screen is always dismissed even on error or hang
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const failsafeTimer = setTimeout(() => {
      const splash = document.getElementById('splash-screen');
      if (splash) {
        splash.classList.add('fade-out');
        setTimeout(() => {
          if (splash.parentNode) splash.parentNode.removeChild(splash);
        }, 600);
      }
    }, 4000);
    return () => clearTimeout(failsafeTimer);
  }, []);

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <LanguageProvider>
        <div className="App">
          <Router>
            <PortraitLock />
            <PWAInstallPrompt />
            <ErrorBoundary>
              <Routes>
                <Route
                  path="/student/analytics-export"
                  element={(
                    <PreferencesProvider>
                      <StudentAnalyticsPage />
                    </PreferencesProvider>
                  )}
                />
                <Route
                  path="*"
                  element={(
                    <Bootstrap>
                      {(initData) => (
                        <LiveClassProvider>
                          <SubjectChapterProvider initialSubjects={initData?.curriculum?.subjects}>
                            <TeacherProvider>
                              <AppInner />
                            </TeacherProvider>
                          </SubjectChapterProvider>
                        </LiveClassProvider>
                      )}
                    </Bootstrap>
                  )}
                />
              </Routes>
            </ErrorBoundary>
          </Router>
          {typeof document !== 'undefined' && createPortal(
            <>
              <Toaster
                position={toastPosition}
                offset={toastPosition === 'bottom-right' ? 24 : 80}
              />
              <ShadcnToaster />
            </>,
            document.body
          )}
        </div>
      </LanguageProvider>
    </GoogleOAuthProvider>
  );
}

const hexToHsl = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
  if (!result) return null;

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `${h} ${s}% ${l}%`;
};

// Inner app that has access to PreferencesContext
function AppInner() {
  const { preferences, config, updatePreferences } = usePreferences();
  const { profile } = useUserProfile();
  const location = useLocation();
  const shouldSkipUserGuide = useMemo(() => {
    const userType = (profile?.user_type || profile?.userType || '').toLowerCase();
    const fullPath = (location.pathname + (location.search || '') + (location.hash || '') + (typeof window !== 'undefined' ? window.location.href : '')).toLowerCase();
    const isB2BRoute = fullPath.includes('/b2b');
    const isTeacherRoute = getPortalType(location.pathname) === 'teacher' || fullPath.includes('/teacher');
    const isParentRoute = fullPath.includes('/parent');
    const isAffiliateRoute = fullPath.includes('/affiliate') || fullPath.includes('/affiliator');
    return (
      userType === 'teacher' ||
      userType === 'guardian' ||
      userType === 'affiliator' ||
      userType === 'institute_admin' ||
      userType === 'b2b' ||
      userType === 'parent' ||
      isB2BRoute ||
      isTeacherRoute ||
      isParentRoute ||
      isAffiliateRoute
    );
  }, [profile, location.pathname, location.search, location.hash]);

  const [showUserGuide, setShowUserGuide] = useState(false);
  const [promoSequenceFinished, setPromoSequenceFinished] = useState(false);

  const ANON_KEY = 'anonymous_guide_seen';
  const navigate = useNavigate();

  // Dynamic Theme Colors Injector (B2B Multi-tenancy / White-labeling)
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const defaults = {
      '--primary': '180 80% 45%',
      '--teal-vivid': '180 80% 45%',
      '--teal-glow': '180 80% 50%',
      '--green-bright': '142 70% 45%',
      '--ring': '180 80% 45%',
    };

    const root = document.documentElement;
    Object.keys(defaults).forEach((key) => {
      const dataAttr = 'default' + key.replace(/^--/, '').replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      if (!root.dataset[dataAttr]) {
        root.dataset[dataAttr] = getComputedStyle(root).getPropertyValue(key).trim() || defaults[key];
      }
    });

    const themeColor = profile?.institute?.theme_color;
    if (themeColor && themeColor.startsWith('#')) {
      const hslValue = hexToHsl(themeColor);
      if (hslValue) {
        const match = hslValue.match(/^(\d+)\s+(\d+)%\s+(\d+)%$/);
        let glowHsl = hslValue;
        let brightHsl = hslValue;
        if (match) {
          const h = parseInt(match[1]);
          const s = parseInt(match[2]);
          const l = parseInt(match[3]);
          glowHsl = `${h} ${s}% ${Math.min(l + 5, 100)}%`;
          brightHsl = `${h} ${Math.min(s + 5, 100)}% ${Math.min(l + 10, 100)}%`;
        }

        root.style.setProperty('--primary', hslValue);
        root.style.setProperty('--teal-vivid', hslValue);
        root.style.setProperty('--teal-glow', glowHsl);
        root.style.setProperty('--green-bright', brightHsl);
        root.style.setProperty('--ring', hslValue);
      }
    } else {
      Object.keys(defaults).forEach((key) => {
        root.style.removeProperty(key);
      });
    }
  }, [profile?.institute?.theme_color]);

  // Handle Deep Linking / Universal Links for Capacitor Native App
  useEffect(() => {
    let appUrlOpenListener = null;

    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('appUrlOpen', (data) => {
        try {
          // Example deep link URL: https://<domain>/s/DgghCxCu
          const url = new URL(data.url);

          if (url.pathname && url.pathname.startsWith('/s/')) {
            // Slight delay helps with cold starts on Android
            setTimeout(() => {
              navigate(url.pathname, { replace: true });
            }, 300);
          }
        } catch (err) {
          console.error('Failed to handle deep link URL:', err);
        }
      }).then(listener => {
        appUrlOpenListener = listener;
      });
    }

    return () => {
      if (appUrlOpenListener) {
        appUrlOpenListener.remove();
      }
    };
  }, [navigate]);

  // Initialize Google Sign-In for Native Platforms early
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const googleClientId = (typeof window !== 'undefined' && window.__ENV && window.__ENV.REACT_APP_GOOGLE_CLIENT_ID) || process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (googleClientId) {
        GoogleSignIn.initialize({
          clientId: googleClientId,
          scopes: ["profile", "email"]
        })
          .catch(err => console.warn('[Native Google Sign-In] Early initialization failed (possibly already initialized):', err));
      }
    }
  }, []);

  // Handle Splash Screen fade-out
  useEffect(() => {
    // Hide native splash immediately after mounting
    if (Capacitor.isNativePlatform()) {
      SplashScreen.hide();
    }

    const splash = document.getElementById('splash-screen');
    if (splash) {
      const timer = setTimeout(() => {
        splash.classList.add('fade-out');
        // Completely remove from DOM after transition finishes
        setTimeout(() => {
          if (splash.parentNode) splash.parentNode.removeChild(splash);
        }, 600);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Handle Global Session Revocation (Enforced Logout)
  useEffect(() => {
    const handleSessionRevoked = (e) => {
      const detail = e.detail;
      const message = detail?.detail || 'You were signed out because your account was logged in on another device';
      const toastId = 'session-revoked';

      // Clear all sessions locally
      const { clearAllSessions } = require('@/utils/auth');
      clearAllSessions();

      // Show toast notification
      toast.error(message, {
        id: toastId,
        duration: 5000,
      });

      // Force redirect after a short delay
      setTimeout(() => {
        window.location.href = '/#/login'; // Use hash path for compatibility
        window.location.reload(); // Force full reload
      }, 1500);
    };

    window.addEventListener('sessionRevoked', handleSessionRevoked);
    return () => window.removeEventListener('sessionRevoked', handleSessionRevoked);
  }, []);


  const hasSeenGuide = useCallback(() => {
    const portal = getPortalType(location.pathname || '');
    const authed = isAuthenticated(portal);
    if (authed) {
      // Check for preferences context first, fallback to basic user data from login
      const userData = getUserData(portal);
      return preferences?.has_seen_user_guide === true || userData?.has_seen_user_guide === true;
    }
    return localStorage.getItem(ANON_KEY) === 'true';
  }, [preferences]);

  const markGuideSeen = useCallback(async () => {
    const portal = getPortalType();
    const authed = isAuthenticated(portal);
    if (authed) {
      try { await preferencesAPI.updatePreferences({ has_seen_user_guide: true }); } catch (_) { }
    } else if (getAnonymousUserPreferences()) {
      localStorage.setItem(ANON_KEY, 'true');
    }
  }, []);

  useEffect(() => {
    const handleShowGuide = (e) => {
      if (shouldSkipUserGuide) return;
      const force = e?.detail?.force === true;
      if (force || (promoSequenceFinished && !hasSeenGuide())) {
        setShowUserGuide(true);
      }
    };
    window.addEventListener('showUserGuide', handleShowGuide);

    // Auto-show for authenticated users who have preferences loaded but haven't seen it yet
    // ONLY show if promo sequence is explicitly finished
    if (!shouldSkipUserGuide && promoSequenceFinished && preferences && preferences.has_seen_user_guide === false) {
      setShowUserGuide(true);
    }

    return () => window.removeEventListener('showUserGuide', handleShowGuide);
  }, [hasSeenGuide, preferences, shouldSkipUserGuide, promoSequenceFinished]);

  const handleGuideClose = useCallback(async () => {
    setShowUserGuide(false);
    await markGuideSeen();
  }, [markGuideSeen]);

  return (
    <>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/" element={<RootRedirect />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/affiliators" element={<AffiliatorAuth initialTab="login" />} />
        <Route path="/affiliators/signup" element={<AffiliatorAuth initialTab="signup" />} />
        <Route path="/affiliators/login" element={<AffiliatorAuth initialTab="login" />} />
        <Route path="/affliators" element={<Navigate to="/affiliators" replace />} />
        <Route path="/affliators/signup" element={<Navigate to="/affiliators/signup" replace />} />
        <Route path="/affliators/login" element={<Navigate to="/affiliators/login" replace />} />
        <Route path="/affiliator" element={<Navigate to="/affiliators" replace />} />
        <Route path="/affiliator/signup" element={<Navigate to="/affiliators/signup" replace />} />
        <Route path="/affiliator/login" element={<Navigate to="/affiliators/login" replace />} />
        <Route path="/affliator" element={<Navigate to="/affiliators" replace />} />
        <Route path="/affliator/signup" element={<Navigate to="/affiliators/signup" replace />} />
        <Route path="/affliator/login" element={<Navigate to="/affiliators/login" replace />} />
        <Route path="/affiliates" element={<Navigate to="/affiliate" replace />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/s/:slug" element={<ShareLinkHandler />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/parent/plans" element={<ParentPlansPage />} />
        <Route path="/parent/analytics" element={<ParentAnalyticsPage />} />
        <Route path="/animation-preview" element={<AnimationPreview />} />
        <Route path="/sim-lab" element={<SimLab />} />
        <Route path="/tuitions" element={<TuitionsRedirect />} />
        <Route path="/book/:tutorId" element={<BookTutorRedirect />} />
        <Route path="/book" element={<BookTutorRedirect />} />

        {/* Student Portal Routes - Persistent Layout */}
        <Route path="/student" element={
          <ProtectedRoute>
            <Layout portal="student" />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="learn" replace />} />
          <Route path="learn" element={<LearnPage />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="tuitions" element={<TuitionsPage />} />
          <Route path="math-problems/:chapterId" element={<MathProblemsPage />} />
          <Route path="math-problems" element={<MathProblemsPage />} />

          <Route path="quests" element={
            <Suspense fallback={<QuestLoadingFallback />}>
              <QuestsPage />
            </Suspense>
          } />

          <Route path="quests/:questId">
            <Route index element={<Navigate to="test" replace />} />
            <Route path="practice" element={
              <QuestsProvider>
                <Suspense fallback={<QuestLoadingFallback title="Loading Practice Mode..." />}>
                  <PracticeModePage />
                </Suspense>
              </QuestsProvider>
            } />
            <Route path="test" element={
              <QuestsProvider>
                <Suspense fallback={<QuestLoadingFallback title="Loading Test Mode..." />}>
                  <TestModePage />
                </Suspense>
              </QuestsProvider>
            } />
            <Route path="results" element={
              <QuestsProvider>
                <Suspense fallback={<QuestLoadingFallback title="Loading Results..." />}>
                  <ResultsPage />
                </Suspense>
              </QuestsProvider>
            } />
          </Route>

          <Route path="curiosity">
            <Route index element={<CuriosityCentre />} />
            <Route path="topics" element={<AdvancedTopicsPage />} />
            <Route path="topics/:id" element={<AdvancedTopicsPage />} />
            <Route path="chapter/:chapterId" element={<ChapterCuriosity />} />
            <Route path="video/:contentId" element={<VideoPlayer />} />
            <Route path="advanced-video/:videoId" element={<VideoPlayer />} />
            <Route path="podcast/:contentId" element={<AudioPlayer />} />
            <Route path="article/:contentId" element={<ArticleDetailPage />} />
            <Route path="advanced-article/:articleId" element={<ArticleDetailPage />} />
            <Route path="course-content/:itemId" element={<CourseContentPage />} />
            <Route path="topic/:id" element={<LegacyCuriosityTopicRedirect />} />
            <Route path="content/:contentId" element={<ContentDetail />} />
          </Route>

          <Route path="profile" element={<ProfilePage />} />
          <Route path="analytics" element={<StudentAnalyticsPage />} />
          <Route path="subscriptions" element={<Navigate to="/student/profile?upgrade=true" replace />} />
          <Route path="achievements/leaderboard" element={<LeaderboardPage />} />
          <Route path="achievements/shop" element={<ShopPage />} />
          <Route path="achievements/founders" element={<StudentFoundersPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="earnings" element={<StudentAffiliateEarnings />} />
          <Route path="affiliate-earnings" element={<Navigate to="/student/earnings" replace />} />
          <Route path="about" element={<AboutPage />} />
        </Route>

        {/* Teacher Portal Routes - Persistent Layout */}
        <Route path="/teacher" element={
          <ProtectedRoute>
            <Layout portal="teacher" />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="teach" replace />} />
          <Route path="dashboard" element={<Navigate to="/teacher/teach" replace />} />
          <Route path="calendar" element={<TeacherCalendar />} />
          <Route path="profile" element={<TeacherProfile />} />
          <Route path="settings" element={<TeacherSettings />} />
          <Route path="subscriptions" element={<Navigate to="/teacher/profile" replace />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="notes" element={<TeacherNotes />} />
          <Route path="library" element={<TeacherLibrary />} />
          <Route path="earnings" element={<TeacherEarnings />} />
          <Route path="affiliate-earnings" element={<TeacherAffiliateEarnings />} />
          <Route path="ambassador-earnings" element={<Navigate to="/teacher/affiliate-earnings" replace />} />
          <Route path="coaching-sessions" element={<TeacherSessions />} />
          <Route path="teach" element={<LearnPage />} />
          <Route path="trainings" element={<TeacherTrainings />} />

          <Route path="ai-tools">
            <Route index element={<TeacherAITools />} />
            <Route path="question-generator" element={<QuestionGenerator />} />
            <Route path="chapter-planner">
              <Route index element={<ChapterPlannerList />} />
              <Route path="create" element={<ChapterTeachingPlanner />} />
              <Route path="current" element={<ChapterTeachingPlanner />} />
              <Route path=":planId" element={<ChapterTeachingPlanner />} />
              <Route path=":planId/periods/:periodNumber" element={<ChapterTeachingPlanner />} />
            </Route>
          </Route>
        </Route>

        {/* B2B Institution Portal Routes */}
        <Route path="/b2b" element={
          <ProtectedRoute>
            <B2BLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<B2BDashboard />} />
          <Route path="students" element={<B2BStudents />} />
          <Route path="students/:studentId/analytics" element={<B2BStudentAnalytics />} />
          <Route path="staff" element={<B2BStaff />} />
          <Route path="coupons" element={<B2BCoupons />} />
          <Route path="commissions" element={<B2BCommissions />} />
          <Route path="profile" element={<B2BProfile />} />
        </Route>

        {/* Affiliate Partner Portal Routes */}
        <Route path="/affiliate" element={
          <ProtectedRoute>
            <AffiliateLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AffiliateDashboard />} />
          <Route path="commissions" element={<AffiliateCommissions />} />
          <Route path="coupons" element={<AffiliateCoupons />} />
          <Route path="settings" element={<AffiliateSettings />} />
        </Route>

        {/* Guardian Portal Routes */}
        <Route path="/guardian" element={
          <ProtectedRoute>
            <GuardianLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<GuardianDashboard />} />
          <Route path="analytics" element={<GuardianChildAnalytics />} />
        </Route>
      </Routes>
    </Suspense>
      <OfflineBanner />
      <GlobalLiveClassOverlay />
      <TeacherRatingModal />
      <UpdateAppModal />
      <PromoModal onComplete={() => setPromoSequenceFinished(true)} />
      <GlobalCelebration />
      <ProfileCompletionCheck />
      {!shouldSkipUserGuide && <UserGuideModal isOpen={showUserGuide} onClose={handleGuideClose} />}
    </>
  );
}

const QuestLoadingFallback = ({ title = "Loading Quests..." }) => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-[hsl(var(--green-bright))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-foreground">{title}</p>
    </div>
  </div>
);

function GlobalLiveClassOverlay() {
  const { sessionData, isLiveClassActive, closeLiveClass, viewMode, minimizeLiveClass, expandLiveClass } = useLiveClass();

  if (!isLiveClassActive || !sessionData) return null;

  return (
    <TutorLiveClassModal
      isOpen={isLiveClassActive}
      onClose={closeLiveClass}
      viewMode={viewMode}
      onMinimize={minimizeLiveClass}
      onExpand={expandLiveClass}
      bookingId={sessionData.bookingId}
      token={sessionData.token}
      livekitUrl={sessionData.livekitUrl}
      sessionDetails={sessionData.sessionDetails}
    />
  );
}

export default App;
